import React, { useState } from 'react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import FormControl from '../../common/FormControl';
import { Send } from 'lucide-react';
import type { LitInstance, LitHistoryEntry } from '../../../services/litInstanceService';
import type { LitProceedingGraph, LitGraphStep } from '../../../services/litigationMasterService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    step_id?: number | null;
    step_label: string;
    step_class_name?: string | null;
    step_class_color?: string | null;
    notes?: string;
    next_due_date?: string | null;
  }) => Promise<void>;
  instance: LitInstance;
  graph: LitProceedingGraph | null;
  isTemplateMode: boolean;
  mode?: 'grc' | 'execute';
  history: LitHistoryEntry[];
}

const RecordStepModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  instance,
  graph,
  isTemplateMode,
  mode = 'grc',
  history,
}) => {
  const [selectedStep, setSelectedStep] = useState<LitGraphStep | null>(null);
  const [freeLabel, setFreeLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [nextDue, setNextDue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isExecute = mode === 'execute';

  // Determine possible next steps for template mode
  const currentStepInGraph = graph?.steps.find(s => s.id === instance.current_step_id);
  const currentStepFlows = graph
    ? graph.flows.filter(f => f.from_step_id === instance.current_step_id)
    : [];
  const nextStepOptions: LitGraphStep[] = currentStepFlows
    .map(f => graph!.steps.find(s => s.id === f.to_step_id))
    .filter((s): s is LitGraphStep => !!s);

  const startSteps: LitGraphStep[] = (!instance.current_step_id && graph)
    ? graph.steps.filter(s => s.is_start)
    : [];

  const flatTemplate = !!graph && graph.flows.length === 0;
  const stepsToShow: LitGraphStep[] = flatTemplate
    ? (graph?.steps ?? [])
    : (instance.current_step_id ? nextStepOptions : startSteps);

  const handleClose = () => {
    setSelectedStep(null);
    setFreeLabel('');
    setNotes('');
    setNextDue('');
    onClose();
  };

  const handleSubmit = async () => {
    const label = isTemplateMode
      ? (selectedStep?.step_name ?? '')
      : freeLabel.trim();
    if (!label) return;

    setSubmitting(true);
    try {
      await onSubmit({
        step_id: isTemplateMode ? selectedStep?.id : null,
        step_label: label,
        step_class_name: isTemplateMode ? selectedStep?.step_class_name : null,
        step_class_color: isTemplateMode ? selectedStep?.step_class_color : null,
        notes: notes.trim() || undefined,
        next_due_date: nextDue || null,
      });
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = isTemplateMode ? !!selectedStep : !!freeLabel.trim();
  const modalTitle = history.length === 0 ? 'Record initial step' : 'Record next step';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>
        {modalTitle}
      </Modal.Header>

      <Modal.Body>
        {/* Template: chips for possible next steps */}
        {isTemplateMode && stepsToShow.length > 0 && (
          <div className="mb-4">
            {isExecute ? (
              <p className="text-[11px] text-gray-500 mb-1.5" data-testid="LBL_lit_current_step">
                {currentStepInGraph
                  ? <>Currently at: <span className="font-semibold text-gray-700">{currentStepInGraph.step_name}</span> → choose next:</>
                  : 'Start — choose the first step:'}
              </p>
            ) : (
              <p className="text-[11px] text-gray-500 mb-1.5">Select next step</p>
            )}
            <div className="flex flex-wrap gap-2">
              {stepsToShow.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSelectedStep(prev => prev?.id === s.id ? null : s)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs transition
                    ${selectedStep?.id === s.id
                      ? 'bg-indigo-600 border-indigo-600 text-white'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-indigo-300'}`}
                  data-testid={`BTN_lit_next_step_${s.id}`}
                >
                  <span className={`inline-flex text-[10px] px-1 py-0 rounded-full border ${
                    selectedStep?.id === s.id
                      ? 'bg-white/20 text-white border-white/30'
                      : getStepBadgeClass(s.step_class_color)
                  }`}>
                    {s.step_class_name}
                  </span>
                  {s.step_name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Template mode — no current step and no start steps */}
        {isTemplateMode && stepsToShow.length === 0 && (
          <p className="text-xs text-gray-400 italic mb-4">
            {instance.current_step_id
              ? 'No further steps defined for this position.'
              : 'No start step defined in this template.'}
          </p>
        )}

        {/* Free-style: Step / Status + Next Due Date in one row */}
        {!isTemplateMode && (
          <div className="grid grid-cols-2 gap-x-3 mb-4">
            <FormControl.Input
              label="Step / Status"
              placeholder="Describe what happened or what is next…"
              value={freeLabel}
              onChange={e => setFreeLabel(e.target.value)}
              data-testid="INP_lit_free_step_label"
            />
            <FormControl.DatePicker
              label="Next Due Date"
              value={nextDue}
              onChange={e => setNextDue(e.target.value)}
              data-testid="INP_lit_next_due"
            />
          </div>
        )}

        {/* Template mode: Date picker only */}
        {isTemplateMode && (
          <div className="grid grid-cols-2 gap-x-3 mb-4">
            <FormControl.DatePicker
              label="Next Due Date"
              value={nextDue}
              onChange={e => setNextDue(e.target.value)}
              data-testid="INP_lit_next_due"
            />
          </div>
        )}

        <FormControl.Textarea
          label="Notes (optional)"
          rows={2}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          data-testid="TXT_lit_step_notes"
        />
      </Modal.Body>

      <Modal.Footer className="justify-end">
        <Button
          variant="fill"
          icon={Send}
          disabled={submitting || !canSubmit}
          onClick={handleSubmit}
          data-testid="BTN_lit_advance_submit"
        >
          Record
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Helper function for badge styling (copied from LitTimeline)
const CLASS_COLORS: Record<string, { badge: string }> = {
  orange: { badge: 'bg-orange-50 text-orange-700 border-orange-200' },
  blue: { badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  yellow: { badge: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  purple: { badge: 'bg-purple-50 text-purple-700 border-purple-200' },
  green: { badge: 'bg-green-50 text-green-700 border-green-200' },
};

const getStepBadgeClass = (c?: string | null) =>
  CLASS_COLORS[c || '']?.badge || 'bg-gray-100 text-gray-700 border-gray-200';

export default RecordStepModal;
